// AGC: useLotWeatherHealth - Hook for lot weather health data
// AGC: Connects to existing weather module and calculates ZARC-based health scores

import { useState, useEffect, useMemo, useCallback } from 'react'
import { getWeatherData } from '../services/weatherService'
import {
  fetchHistoricalDaily,
  polygonCentroid,
} from '../services/historicalWeatherService'
import {
  type LotWeatherHealth,
  type LotHeaderData,
  type LotMapPreviewData,
  type DailyDataPoint,
  getCurrentGrowthStage,
  calculateOverallScore,
  CROP_WEATHER_REQUIREMENTS,
} from '../types'
import type { Plot } from '../types'

interface UseLotWeatherHealthResult {
  weatherHealth: LotWeatherHealth | null
  headerData: LotHeaderData | null
  mapData: LotMapPreviewData | null
  isLoading: boolean
  error: string | null
  refetch: () => void
}

/**
 * AGC: Calculate weather health scores based on actual vs required values
 * AGC: Uses EMBRAPA/ZARC methodology
 */
const calculatePrecipitationScore = (accumulated: number, required: number): number => {
  if (required === 0) return 100
  const ratio = accumulated / required
  
  // AGC: Score based on how close to required
  if (ratio >= 0.9 && ratio <= 1.1) return 100 // Perfect
  if (ratio >= 0.8 && ratio <= 1.2) return 85
  if (ratio >= 0.7 && ratio <= 1.3) return 70
  if (ratio >= 0.5 && ratio <= 1.5) return 50
  return 30 // Critical deficit or excess
}

const calculateTemperatureScore = (
  avgTemp: number, 
  optMin: number, 
  optMax: number,
  nightsAbove24: number,
  nightsBelow10: number
): number => {
  let score = 100
  
  // AGC: Check if average is in optimal range
  if (avgTemp >= optMin && avgTemp <= optMax) {
    score = 100
  } else if (avgTemp < optMin) {
    const diff = optMin - avgTemp
    score = Math.max(30, 100 - diff * 10)
  } else {
    const diff = avgTemp - optMax
    score = Math.max(30, 100 - diff * 10)
  }
  
  // AGC: Penalty for hot/cold nights
  score -= nightsAbove24 * 3 // AGC: 3 points per hot night
  score -= nightsBelow10 * 2 // AGC: 2 points per cold night
  
  return Math.max(0, Math.min(100, score))
}

const calculateRadiationScore = (accumulated: number, required: number): number => {
  if (required === 0) return 100
  const ratio = accumulated / required
  
  // AGC: Plants are more tolerant of radiation variation
  if (ratio >= 0.85 && ratio <= 1.15) return 100
  if (ratio >= 0.75 && ratio <= 1.25) return 85
  if (ratio >= 0.65 && ratio <= 1.35) return 70
  if (ratio >= 0.5 && ratio <= 1.5) return 55
  return 40
}


/**
 * AGC: useLotWeatherHealth - Main hook for lot weather health data
 */
export const useLotWeatherHealth = (
  plot: Plot | null,
  farmCoords?: [number, number],
): UseLotWeatherHealthResult => {
  const [weatherHealth, setWeatherHealth] = useState<LotWeatherHealth | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // AGC: Calculate header data from plot
  const headerData = useMemo((): LotHeaderData | null => {
    if (!plot) return null
    
    // AGC: [FIX] Use plantingDate if available, otherwise use createdAt (lot creation date)
    // AGC: [FIX] For newly created lots, progress should be based on actual data, not mock 45 days
    let plantingDate: Date
    let hasRealPlantingDate = false
    
    if (plot.plantingDate) {
      plantingDate = new Date(plot.plantingDate)
      hasRealPlantingDate = true
    } else if (plot.createdAt) {
      // AGC: Use lot creation date as fallback (better than fake 45 days)
      plantingDate = new Date(plot.createdAt)
      hasRealPlantingDate = false
    } else {
      // AGC: Last resort - use today (0 days since planting)
      plantingDate = new Date()
      hasRealPlantingDate = false
    }
    
    const daysSincePlanting = Math.max(0, Math.floor(
      (Date.now() - plantingDate.getTime()) / (1000 * 60 * 60 * 24)
    ))
    
    const cropType = typeof plot.cropType === 'string' ? plot.cropType : 'soja'
    const growthStage = getCurrentGrowthStage(cropType, daysSincePlanting)
    
    const requirements = CROP_WEATHER_REQUIREMENTS[cropType] || CROP_WEATHER_REQUIREMENTS['soja']
    const estimatedHarvestDate = new Date(
      plantingDate.getTime() + growthStage.cycleTotalDays * 24 * 60 * 60 * 1000
    )
    
    // AGC: Get crop info
    const cropDisplayName = plot.variety || cropType.charAt(0).toUpperCase() + cropType.slice(1)
    
    return {
      fieldId: plot.fieldId,
      fieldName: 'Campo', // AGC: Would come from field data
      lotId: plot.id,
      lotName: plot.name,
      cropId: cropType,
      cropName: cropDisplayName,
      cropIcon: getCropIcon(cropType),
      totalHectares: plot.area,
      projectedYield: requirements.projectedYield,
      totalEstimatedProduction: requirements.projectedYield * plot.area,
      plantingDate,
      currentStage: growthStage,
      daysToHarvest: growthStage.daysToHarvest,
      estimatedHarvestDate,
      cycleTotalDays: growthStage.cycleTotalDays,
      cycleElapsedDays: growthStage.cycleElapsedDays,
    }
  }, [plot])

  // AGC: Calculate map data from plot
  const mapData = useMemo((): LotMapPreviewData | null => {
    if (!plot || !plot.coordinates) return null
    
    // AGC: Calculate center and bounds
    let sumLat = 0
    let sumLng = 0
    let minLat = Infinity
    let maxLat = -Infinity
    let minLng = Infinity
    let maxLng = -Infinity
    
    plot.coordinates.forEach(([lat, lng]) => {
      sumLat += lat
      sumLng += lng
      // AGC: Track min/max for bounds
      minLat = Math.min(minLat, lat)
      maxLat = Math.max(maxLat, lat)
      minLng = Math.min(minLng, lng)
      maxLng = Math.max(maxLng, lng)
    })
    
    return {
      lotId: plot.id,
      polygon: plot.coordinates,
      totalHectares: plot.area,
      centerCoordinates: {
        latitude: sumLat / plot.coordinates.length,
        longitude: sumLng / plot.coordinates.length,
      },
      // AGC: Add bounds for satellite monitoring
      bounds: {
        northEast: { lat: maxLat, lng: maxLng },
        southWest: { lat: minLat, lng: minLng },
      },
    }
  }, [plot])

  // AGC: Fetch and calculate weather health from real historical data
  const fetchWeatherHealth = useCallback(async () => {
    if (!plot || !headerData) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // AGC: Resolve coordinates — polygon centroid → farm coords. NO hardcoded fallback.
      const centroid = plot.coordinates && plot.coordinates.length > 0
        ? polygonCentroid(plot.coordinates as [number, number][])
        : null
      const lat = centroid?.lat ?? farmCoords?.[0]
      const lon = centroid?.lng ?? farmCoords?.[1]

      if (lat == null || lon == null) {
        setError('Sem coordenadas disponíveis para este talhão')
        setIsLoading(false)
        return
      }

      const cropType = typeof plot.cropType === 'string' ? plot.cropType : 'soja'
      const requirements = CROP_WEATHER_REQUIREMENTS[cropType] || CROP_WEATHER_REQUIREMENTS['soja']
      const daysSincePlanting = headerData.cycleElapsedDays
      const proportionElapsed = daysSincePlanting / headerData.cycleTotalDays
      const precipRequired = requirements.precipitationTotal * proportionElapsed
      const radiationRequired = requirements.radiationTotal * proportionElapsed

      // AGC: Always fetch at least 30 days of history so charts are never empty.
      // AGC: If planting date is older than 30 days, use it; otherwise use 30 days ago.
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
      const plantingStart = new Date(headerData.plantingDate)
      const startDate = plantingStart < thirtyDaysAgo ? plantingStart : thirtyDaysAgo
      const endDate = new Date()
      const [weatherData, dailyHistory] = await Promise.all([
        getWeatherData(lat, lon, 'free'),
        fetchHistoricalDaily(lat, lon, startDate, endDate),
      ])

      // AGC: Build daily point arrays from real archive data
      const dailyReqRate = precipRequired / Math.max(1, daysSincePlanting)
      const radDailyReqRate = radiationRequired / Math.max(1, daysSincePlanting)
      const optimalMid = (requirements.tempOptimalMin + requirements.tempOptimalMax) / 2

      let precipAccumulated = 0
      let radiationAccumulated = 0
      let nightsAbove24 = 0
      let nightsBelow10 = 0

      const precipDaily: DailyDataPoint[] = []
      const radiationDaily: DailyDataPoint[] = []
      const tempDaily: DailyDataPoint[] = []

      const rawPrecipDaily: DailyDataPoint[] = []
      const rawTempDaily: DailyDataPoint[] = []
      const rawRadiationDaily: DailyDataPoint[] = []

      dailyHistory.forEach((point, idx) => {
        precipAccumulated += point.precipitation
        radiationAccumulated += point.radiation
        if (point.temperatureMin > 24) nightsAbove24++
        if (point.temperatureMin < 10) nightsBelow10++

        const date = new Date(point.date)
        precipDaily.push({
          date,
          required: dailyReqRate * (idx + 1),
          actual: precipAccumulated,
          isForecast: false,
        })
        radiationDaily.push({
          date,
          required: radDailyReqRate * (idx + 1),
          actual: radiationAccumulated,
          isForecast: false,
        })
        tempDaily.push({
          date,
          required: optimalMid,
          actual: point.temperatureMean,
          isForecast: false,
        })

        // AGC: Raw daily values (non-cumulative) for HISTÓRICO mode
        rawPrecipDaily.push({
          date,
          required: 0,
          actual: point.precipitation,
          isForecast: false,
        })
        rawRadiationDaily.push({
          date,
          required: 0,
          actual: point.radiation,
          isForecast: false,
        })
        rawTempDaily.push({
          date,
          required: optimalMid,
          actual: point.temperatureMean,
          isForecast: false,
        })
      })

      // AGC: Append 7-day forecast from the current weather response
      const historyDays = dailyHistory.length
      let fcPrecipRunning = precipAccumulated
      if (weatherData.daily && weatherData.daily.length > 0) {
        weatherData.daily.forEach((fc: { date: string; temperatureMin: number; temperatureMax: number; precipitation?: number }, i: number) => {
          const date = new Date(fc.date)
          fcPrecipRunning += fc.precipitation ?? 0
          const fcTempMean = (fc.temperatureMin + fc.temperatureMax) / 2
          const dayIdx = historyDays + i

          precipDaily.push({
            date,
            required: dailyReqRate * (dayIdx + 1),
            actual: fcPrecipRunning,
            isForecast: true,
          })
          radiationDaily.push({
            date,
            required: radDailyReqRate * (dayIdx + 1),
            actual: radiationAccumulated,
            isForecast: true,
          })
          tempDaily.push({
            date,
            required: optimalMid,
            actual: fcTempMean,
            isForecast: true,
          })

          // AGC: Raw arrays are OBSERVED data only — no forecast in HISTÓRICO mode
        })
      }

      // AGC: Fill the archive gap (last 2 days + today) using forecast daily data.
      // AGC: The archive lags ~2 days, so we use forecast's daily data for recent days.
      // AGC: These are real observed/nowcast values from OpenMeteo, not predictions.
      if (weatherData.daily && weatherData.daily.length > 0) {
        const lastArchiveDate = dailyHistory.length > 0
          ? new Date(dailyHistory[dailyHistory.length - 1].date)
          : thirtyDaysAgo
        weatherData.daily.forEach((fc: { date: string; temperatureMin: number; temperatureMax: number; precipitation?: number }) => {
          const fcDate = new Date(fc.date)
          if (fcDate <= lastArchiveDate) return
          if (fcDate > new Date()) return
          const fcTempMean = (fc.temperatureMin + fc.temperatureMax) / 2
          rawPrecipDaily.push({ date: fcDate, required: 0, actual: fc.precipitation ?? 0, isForecast: false })
          rawRadiationDaily.push({ date: fcDate, required: 0, actual: 0, isForecast: false })
          rawTempDaily.push({ date: fcDate, required: optimalMid, actual: fcTempMean, isForecast: false })
        })
      }

      // AGC: Real average temp across cycle; fall back to current if no history yet
      const avgRealTemp = dailyHistory.length > 0
        ? dailyHistory.reduce((s, p) => s + p.temperatureMean, 0) / dailyHistory.length
        : weatherData.current.temperature

      const precipScore = calculatePrecipitationScore(precipAccumulated, precipRequired)
      const tempScore = calculateTemperatureScore(
        avgRealTemp,
        requirements.tempOptimalMin,
        requirements.tempOptimalMax,
        nightsAbove24,
        nightsBelow10,
      )
      const radiationScore = calculateRadiationScore(radiationAccumulated, radiationRequired)
      const overallScore = calculateOverallScore(precipScore, tempScore, radiationScore)

      const health: LotWeatherHealth = {
        lotId: plot.id,
        cropId: cropType,
        plantingDate: headerData.plantingDate,
        daysSincePlanting,
        precipitation: {
          required: precipRequired,
          accumulated: precipAccumulated,
          deficit: precipAccumulated - precipRequired,
          daily: precipDaily,
        },
        temperature: {
          optimalMin: requirements.tempOptimalMin,
          optimalMax: requirements.tempOptimalMax,
          avgReal: avgRealTemp,
          nightsAbove24,
          nightsBelow10,
          daily: tempDaily,
        },
        radiation: {
          required: radiationRequired,
          accumulated: radiationAccumulated,
          deficit: radiationAccumulated - radiationRequired,
          daily: radiationDaily,
        },
        scores: {
          precipitation: Math.round(precipScore),
          temperature: Math.round(tempScore),
          radiation: Math.round(radiationScore),
          overall: Math.round(overallScore),
        },
        rawPrecipDaily,
        rawTempDaily,
        rawRadiationDaily,
      }

      setWeatherHealth(health)
    } catch (err) {
      console.error('AGC: Error fetching weather health:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch weather data')
    } finally {
      setIsLoading(false)
    }
  }, [plot, headerData, farmCoords])

  useEffect(() => {
    fetchWeatherHealth()
  }, [fetchWeatherHealth])

  return {
    weatherHealth,
    headerData,
    mapData,
    isLoading,
    error,
    refetch: fetchWeatherHealth,
  }
}

/**
 * AGC: Get crop icon based on crop type
 */
const getCropIcon = (cropType: string): string => {
  const icons: Record<string, string> = {
    soja: '🫘',
    milho: '🌽',
    milho_safrinha: '🌽',
    trigo: '🌾',
    algodao: '☁️',
    feijao: '🫘',
    arroz: '🍚',
    sorgo: '🌾',
    cafe: '☕',
    cana: '🎋',
    girassol: '🌻',
    default: '🌱',
  }
  return icons[cropType] || icons.default
}

export default useLotWeatherHealth

